import { useEffect, useState } from 'react';
import { useParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';

import {
  DataItem,
  DataPanelItem,
  DebugView,
  FaIcon,
  LoadingFailedBox,
  NgBreadcrumbItem,
  NgBreadcrumbs,
  NgButton,
  NgPageHeader,
} from '@/ui';
import { incidentDetailRoute } from '@/features/shared/routes';
import { DateFormatter } from '@/features/shared/DateFormatter';
import { getVideoOptions } from '@/lib/video/config';
import { checkRecordingExists } from '@/lib/video/recordingAvailabilityService';
import { destroyHls, downloadRecording, loadRecording } from '@/lib/video/recordingDetailPlaybackService';
import { fetchRecording, recordingDetailQueryKey } from './recordingsApi';
import { incidentRecordingsRoute } from './routes';
import { VideoRecordingDetailLoadingPlaceholder } from './VideoRecordingDetailLoadingPlaceholder';

export function RecordingDetailPage() {
  const { t } = useTranslation();
  const { incidentId = '', videoId = '' } = useParams<{ incidentId: string; videoId: string }>();

  const pageTitle = t('PageTitle_VideoRecordingDetail');

  const {
    data: recording,
    isError: showError,
    isFetching: isLoading,
  } = useQuery({
    queryKey: recordingDetailQueryKey(incidentId, videoId),
    queryFn: () => fetchRecording(incidentId, videoId),
  });

  const [videoServerUrl, setVideoServerUrl] = useState<string | null>(null);
  useEffect(() => {
    setVideoServerUrl(getVideoOptions().VIDEO_SERVER_URL);
  }, []);

  const [recordingBlobExists, setRecordingBlobExists] = useState(true);
  const [shouldLoadRecording, setShouldLoadRecording] = useState(false);
  useEffect(() => {
    if (recording == null) {
      return;
    }

    let cancelled = false;
    void checkRecordingExists(videoId).then((exists) => {
      if (cancelled) {
        return;
      }
      setRecordingBlobExists(exists);
      if (exists) {
        setShouldLoadRecording(true);
      }
    });

    return () => {
      cancelled = true;
      destroyHls();
    };
  }, [recording, videoId]);

  useEffect(() => {
    if (!shouldLoadRecording) {
      return;
    }
    setShouldLoadRecording(false);
    loadRecording(videoId);
  }, [shouldLoadRecording, videoId]);

  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadErrorMessage, setDownloadErrorMessage] = useState<string | null>(null);
  const [downloadProgress, setDownloadProgress] = useState(0);

  const downloadVideo = async () => {
    if (recording == null || isDownloading) {
      return;
    }

    setIsDownloading(true);
    setDownloadErrorMessage(null);
    setDownloadProgress(0);

    try {
      const filename = `incident_${incidentId}_video_${videoId}.mp4`;
      await downloadRecording(videoId, filename, (progress) => {
        setDownloadProgress(Math.min(Math.max(progress, 0), 100));
      });
    } catch (error) {
      setDownloadErrorMessage(error instanceof Error ? error.message : String(error));
    } finally {
      setIsDownloading(false);
    }
  };

  const serverStatsUrl = `${videoServerUrl ?? ''}/${videoId}/stats/server`;
  const consumerStatsUrl = `${videoServerUrl ?? ''}/${videoId}/stats/consumer`;
  const producerStatsUrl = `${videoServerUrl ?? ''}/${videoId}/stats/producer`;
  const logsUrl = `${videoServerUrl ?? ''}/${videoId}/stats/logs`;

  return (
    <div className="content p-3">
      <NgBreadcrumbs>
        <NgBreadcrumbItem title={t('PageTitle_Incident')} to={incidentDetailRoute(incidentId)} />
        <NgBreadcrumbItem title={t('PageTitle_ListOfRecordings')} to={incidentRecordingsRoute(incidentId)} />
      </NgBreadcrumbs>

      <DebugView>
        {videoServerUrl != null && videoServerUrl !== '' && (
          <DataPanelItem title="Stats">
            <span>
              <a href={serverStatsUrl} target="_blank">
                Server
              </a>
              <a href={consumerStatsUrl} target="_blank">
                Consumer
              </a>
              <a href={producerStatsUrl} target="_blank">
                Producer
              </a>
              <a href={logsUrl} target="_blank">
                Logs
              </a>
            </span>
          </DataPanelItem>
        )}
      </DebugView>

      <NgPageHeader title={pageTitle} />
      <h1>{pageTitle}</h1>

      {isLoading ? (
        <VideoRecordingDetailLoadingPlaceholder />
      ) : showError ? (
        <LoadingFailedBox />
      ) : recording == null || !recordingBlobExists ? (
        <div className="bg-warning-subtle border border-1 border-warning-subtle border-dashed px-3 py-5 text-warning rounded-1 text-center">
          <FaIcon icon="fal fa-warning" />
          {t('Video_NoRecording_CallerDidNotConnect')}
        </div>
      ) : (
        <>
          <div className="bg-light d-flex justify-content-center align-items-center">
            <video
              controls
              style={{ width: 'auto', aspectRatio: '9/16', maxWidth: '100%', maxHeight: 'calc(100svh - 220px)' }}
              id="recordingDetailVideo"
              className="rounded-1"
            >
              Your browser does not support the video tag.
            </video>
          </div>

          <div
            id="recordingUnavailable"
            style={{ display: 'none' }}
            className="bg-warning-subtle border border-1 border-warning-subtle border-dashed px-3 py-5 text-warning rounded-1 text-center"
          >
            <FaIcon icon="fal fa-warning" />
            {t('Incident_VideoRecordings_NotAvailable')}
          </div>

          <div className="mt-2 d-flex flex-row align-items-center justify-content-between gap-2">
            <div>
              <DataItem title={t('Incident_VideoRecordings_RecordingDate')}>
                <div className="small">
                  <DateFormatter date={recording.startTimeUtc} formatType="calendar" />
                </div>
              </DataItem>
            </div>
            <div className="flex-shrink-0">
              <NgButton
                text={isDownloading ? t('Button_Downloading') : t('Button_Download')}
                onClick={downloadVideo}
                showProgress={isDownloading}
                disabled={isDownloading}
                icon="fal fa-download"
                outline
              />
            </div>
          </div>

          {isDownloading && (
            <div className="mt-3">
              <div className="d-flex justify-content-between mb-1">
                <small className="text-muted">{t('Video_DownloadProgress')}</small>
                <small className="text-muted">{downloadProgress}%</small>
              </div>
              <div className="progress" style={{ height: '8px' }}>
                <div
                  className="progress-bar progress-bar-striped progress-bar-animated"
                  role="progressbar"
                  style={{ width: `${downloadProgress}%` }}
                  aria-valuenow={downloadProgress}
                  aria-valuemin={0}
                  aria-valuemax={100}
                ></div>
              </div>
            </div>
          )}

          {downloadErrorMessage != null && downloadErrorMessage !== '' && (
            <div className="alert alert-danger mt-3" role="alert">
              <FaIcon icon="fal fa-exclamation-triangle" />
              {t('Video_DownloadError')}
              <div className="small mt-1">{downloadErrorMessage}</div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
